import {
	useLoaderData,
	useLocation,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { Checkbox } from '#app/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Label } from '#app/components/ui/label'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { FilterButton } from '#app/features/plp-clerk/clerk-components/filters/filter-button'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { subtractArrays } from '#app/utils/array-utils'
import { cn } from '#app/utils/misc'
import { sortClerkFacetBySize, sortClerkFacets } from '#app/utils/search/sizes'
import FilterDropdownContent from './filter-dropdown-content'

export function SizesDropdown() {
	const [isOpen, setisOpen] = useState(false)

	const data = useLoaderData<typeof loader>()
	const location = useLocation()
	const navigate = useNavigate()
	const navigation = useNavigation()

	let { t } = useTranslation('common')
	// Define the styles for the expanded state
	const sizeFacets = (data.facetsData?.facets?.sizes as ClerkFacet[]).filter(
		facet => facet.value !== '',
	)

	const isKids = location.pathname.includes('kids') ?? false

	// Toggle the expanded state when the dropdown is opened or closed
	const toggleDropdown = () => {
		setisOpen(!isOpen)
	}

	const handleCheckboxChange = (
		checked: boolean,
		term: string[],
		key: string,
	) => {
		const params = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)

		if (checked) {
			term.forEach(value => params.append(key, value))
		} else {
			const values = params.getAll(key)
			const newValues = subtractArrays(values, term)
			params.delete(key)
			newValues.forEach(value => {
				params.append(key, value)
			})
		}
		// remove the pagination
		params.delete('p')
		navigate({
			pathname: '.',
			search: params.toString(),
		})
	}

	const kidsFormattedSizes = sizeFacets.sort(sortClerkFacetBySize)

	const isNumberSize = (size: string) => /^\d+$/.test(size)
	const internationalSizes = sizeFacets
		.filter((facet: ClerkFacet) => !isNumberSize(facet.value))
		.sort(sortClerkFacets)
	const euSizes = sizeFacets
		.filter((facet: ClerkFacet) => isNumberSize(facet.value))
		.sort(sortClerkFacets)

	const getSelectedCount = () => {
		const sizes = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		).getAll('size')

		// filter the sizes if they are not on the sizeFacets
		const selectedSizes = sizes.filter(size =>
			sizeFacets.some(facet => facet.value === size),
		)
		return selectedSizes.length
	}
	return (
		<DropdownMenu onOpenChange={toggleDropdown}>
			<DropdownMenuTrigger asChild>
				<FilterButton
					selectedCount={getSelectedCount()}
					isSelectorOpen={isOpen}
					text={t('size')}
				/>
			</DropdownMenuTrigger>
			<FilterDropdownContent>
				<motion.div
					initial={{ opacity: 0, maxHeight: 0 }}
					animate={{ opacity: 1, maxHeight: '240px' }}
					exit={{ opacity: 0, maxHeight: 0 }}
					transition={{ duration: 0.3, ease: 'linear' }}
				>
					{
						// !kids here (
						!isKids && (
							<div className="flex flex-col">
								{/* International Sizes */}
								<div className="gap-4">
									<PlentyBody size="lg" className="px-4 pt-2 font-bold">
										{t('clothing_size')}
									</PlentyBody>
									<PlentyBody
										size="md"
										className={cn(
											'px-4 pb-2 text-green-40',
											internationalSizes.length > 0 ? 'block' : 'hidden',
										)}
									>
										International
									</PlentyBody>
									{internationalSizes?.map(
										(facet: ClerkFacet, index: number) => (
											<CheckboxSizeFilter
												facet={facet}
												handleCheckboxChange={handleCheckboxChange}
												key={facet.name}
												className="px-4 py-2 hover:bg-beige-70"
											/>
										),
									)}
								</div>

								{/* EU Sizes */}
								<div>
									<PlentyBody
										size="md"
										className={cn(
											'px-4 pt-2 text-green-40',
											euSizes.length > 0 ? 'block' : 'hidden',
										)}
									>
										{t('product_list_page:eu_sizes')}
									</PlentyBody>
									{!isKids &&
										euSizes?.map((facet: ClerkFacet) => (
											<CheckboxSizeFilter
												facet={facet}
												handleCheckboxChange={handleCheckboxChange}
												key={facet.name}
												className="px-4 py-2 hover:bg-beige-70"
											/>
										))}
								</div>
							</div>
						)
					}
					<div>
						{isKids &&
							kidsFormattedSizes?.map((facet: ClerkFacet) => (
								<CheckboxSizeFilter
									facet={facet}
									handleCheckboxChange={handleCheckboxChange}
									key={facet.name}
									className="px-4 py-2 hover:bg-beige-70"
								/>
							))}
					</div>
				</motion.div>
			</FilterDropdownContent>
		</DropdownMenu>
	)
}

export const CheckboxSizeFilter = ({
	facet,
	handleCheckboxChange,
	className,
}: {
	facet: ClerkFacet
	handleCheckboxChange: (checked: boolean, term: string[], key: string) => void
	className?: string
}) => {
	const location = useLocation()
	const navigation = useNavigation()

	return (
		<Label
			className={cn('flex items-center hover:cursor-pointer', className)}
			htmlFor={facet.value}
		>
			<Checkbox
				className="mr-2 h-6 w-6 "
				id={facet.name}
				value={facet.value}
				checked={new URLSearchParams(
					navigation.state === 'idle'
						? location.search
						: navigation.location.search,
				)
					.getAll('size')
					.includes(facet.value)}
				name="size"
				defaultChecked={new URLSearchParams(
					navigation.state === 'idle'
						? location.search
						: navigation.location.search,
				)
					.getAll('size')
					.includes(facet.value)}
				onCheckedChange={checked =>
					handleCheckboxChange(
						checked,
						facet.terms ? facet.terms : [facet.value],
						'size',
					)
				}
			/>
			{/* TODO: Make a helper component for this i have seen this code in multiple places */}
			<div className="flex w-full justify-between gap-2">
				<PlentyBody size={'sm'}>{facet.value}</PlentyBody>
				<PlentyBody size={'sm'} className="text-green-40">
					{facet.count}
				</PlentyBody>
			</div>
		</Label>
	)
}
